/**
 * Phase 8 (applicant-web slice) — PostSubmitView.
 *
 * Renders a friendly status surface when the case has moved past
 * `filling`. The applicant sees their submit timestamp, the current
 * case state as a color-coded chip, an estimated packet-ship date
 * (submitted_at + 72hr per the locked SLA), and state-specific
 * helper copy explaining what happens next.
 *
 * The component is intentionally pure (props-only); the App owns
 * fetching `caseState` and `submittedAtMs` from the persistence
 * adapter. `nowMs` is overridable so tests can pin time
 * deterministically.
 */
import { useCallback, useId, useState } from 'react';

import {
  caseStateLabel,
  type TPostSubmitState,
} from './caseState';
import { getCaseStateBadgeTokens } from './caseStateBadge';
import styles from './PostSubmitView.module.css';

const HOUR_MS = 60 * 60 * 1000;
/**
 * Locked product decision: 72 hours from submit click to printed
 * packet shipped to the applicant. Mirrors `SLA_HOURS` in
 * admin-web's `caseState.ts`.
 */
const SLA_HOURS = 72;
const SLA_MS = SLA_HOURS * HOUR_MS;

export interface IPostSubmitViewProps {
  caseState: TPostSubmitState;
  /** ms-epoch the application transitioned to `submitted`. */
  submittedAtMs: number;
  /** Optional override (defaults to `Date.now()` on render). */
  nowMs?: number;
  /**
   * USPS / FedEx tracking number captured by the case team at
   * shipment time. Surfaced inline only when caseState is `shipped`
   * and a value is present; absent values render nothing rather
   * than an empty placeholder.
   */
  trackingNumber?: string | null;
  /**
   * ms-epoch the case team marked the packet as shipped. Used in the
   * shipped-state body copy so the applicant sees "We mailed your
   * packet on …". When absent, we fall back to the submit date so
   * the copy still reads naturally.
   */
  shippedAtMs?: number | null;
  /**
   * Storage path of the latest packet PDF. When present, the view
   * renders a download section below the helper block, regardless
   * of caseState — case team may upload during `docket_ready`
   * before `printing`, and the applicant should see it immediately.
   */
  packetStoragePath?: string;
  /** Original filename for the download UX. */
  packetFileName?: string;
  /** Bytes — used for the "12.4 MB" display next to the filename. */
  packetSizeBytes?: number;
  /**
   * Click handler that resolves a signed URL and triggers the
   * download. Lazy on click — URLs expire, so the App.tsx /
   * FirebaseApp wiring must NOT pre-fetch on mount.
   */
  onDownloadPacket?: () => Promise<void>;
}

interface ICopyEntry {
  headline: string;
  body: string;
}

const COPY_BY_STATE: Record<TPostSubmitState, ICopyEntry> = {
  submitted: {
    headline: 'Your application is with our case team',
    body: "We received your application on {date}. We're reviewing it now and will follow up if anything's unclear.",
  },
  in_review: {
    headline: 'Reviewing your application',
    body: "Our case team is going through your answers and supporting documents. We'll reach out via email if we need clarification on anything.",
  },
  docket_ready: {
    headline: 'Building your packet',
    body: "We've finished review and are assembling the printed forms + your supporting documents into a single packet ready to mail to USCIS.",
  },
  printing: {
    headline: 'Printing your packet',
    body: "Your packet is on the print queue. We'll ship it within the next 24 hours.",
  },
  shipped: {
    headline: 'Your packet is on the way',
    body: "We mailed your packet on {shipDate}. You'll receive it in 2-4 business days. Sign every page marked with a signature line in BLUE INK, then forward to USCIS at the address on the cover sheet.",
  },
};

/**
 * Format an ms-epoch as a locale-friendly long date (e.g. "May 5,
 * 2026" in en-US). We deliberately read `navigator.language` rather
 * than hardcoding `'en-US'` so applicants in other locales see a
 * format their browser prefers; the test environment falls back to
 * the platform default.
 */
function formatDate(ms: number): string {
  const date = new Date(ms);
  const locale =
    typeof navigator !== 'undefined' && navigator.language
      ? navigator.language
      : 'en-US';
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

function formatBytes(bytes: number | undefined): string | null {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes <= 0) {
    return null;
  }
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function escapeRegex(input: string): string {
  return input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function interpolate(
  template: string,
  values: Record<string, string>,
): string {
  let out = template;
  for (const [key, value] of Object.entries(values)) {
    out = out.replace(new RegExp(escapeRegex(`{${key}}`), 'g'), value);
  }
  return out;
}

export function PostSubmitView(props: IPostSubmitViewProps): JSX.Element {
  const {
    caseState,
    submittedAtMs,
    nowMs = Date.now(),
    trackingNumber,
    shippedAtMs,
    packetStoragePath,
    packetFileName,
    packetSizeBytes,
    onDownloadPacket,
  } = props;

  const [downloading, setDownloading] = useState(false);
  const handleDownloadClick = useCallback(async () => {
    if (!onDownloadPacket) return;
    setDownloading(true);
    try {
      await onDownloadPacket();
    } finally {
      setDownloading(false);
    }
  }, [onDownloadPacket]);

  const headingId = useId();
  const tokens = getCaseStateBadgeTokens(caseState);
  const copy = COPY_BY_STATE[caseState];

  const submittedDateText = formatDate(submittedAtMs);
  const shipDateText =
    shippedAtMs !== undefined && shippedAtMs !== null
      ? formatDate(shippedAtMs)
      : submittedDateText;

  // 72-hour SLA estimate. When the window has elapsed without the
  // case team marking the packet shipped, we fall back to a softer
  // "ASAP" string rather than letting the date crawl into the past.
  const estimatedShipMs = submittedAtMs + SLA_MS;
  const slaElapsed = nowMs > estimatedShipMs;
  const estimateLabel: string = slaElapsed
    ? "ASAP — we're working on it"
    : formatDate(estimatedShipMs);

  const bodyText = interpolate(copy.body, {
    date: submittedDateText,
    shipDate: shipDateText,
  });

  // Derive a state-specific "what now" hint instead
  // of repeating the body text in the helper block. For pre-shipping
  // states the applicant has nothing to do but wait; the shipping
  // state has a real checklist and (optional) tracking number.
  const hasShippedActions = caseState === 'shipped';
  const trackingPresent =
    trackingNumber !== undefined &&
    trackingNumber !== null &&
    trackingNumber.length > 0;

  return (
    <main
      className={styles.shell}
      aria-labelledby={headingId}
    >
      <article className={`${styles.card} reveal_on_mount`}>
        <p className={styles.eyebrow}>
          <span className={styles.eyebrow__icon} aria-hidden="true">
            ✓
          </span>
          <span>Submitted on {submittedDateText}</span>
        </p>
        <h1 id={headingId} className={styles.headline}>
          {copy.headline}
        </h1>
        <p className={styles.body}>{bodyText}</p>

        {/*
          Wave 4 Slice 14 (2026-05-02) — meta row collapses the previous
          stacked status + ship-date blocks into a single two-column
          panel. Reads as a "card status header" rather than two
          discrete labels with vertical breathing room between them.
          On narrow viewports the row wraps to two stacked rows.
        */}
        <div className={styles.metaPanel}>
          <div className={styles.metaCell}>
            <span className={styles.metaLabel}>Status</span>
            <span
              role="status"
              data-case-state-badge=""
              data-case-state-color={tokens.colorName}
              className={styles.badge}
              style={{
                background: tokens.background,
                borderColor: tokens.border,
                color: tokens.text,
              }}
            >
              {caseStateLabel(caseState)}
            </span>
          </div>
          <div className={styles.metaDivider} aria-hidden="true" />
          <div className={styles.metaCell}>
            <span className={styles.metaLabel}>
              {hasShippedActions ? 'Shipped' : 'Estimated ship'}
            </span>
            <span className={styles.metaValue}>
              {hasShippedActions ? shipDateText : estimateLabel}
            </span>
          </div>
        </div>

        {/*
          Wave 4 Slice 14 (2026-05-02) — single state-specific action
          block. Pre-shipping states say "you don't have to do
          anything"; shipping says "here's what to do when it arrives"
          with the full checklist + tracking number. No more redundant
          repetition of the upper body paragraph.
        */}
        <section className={styles.actionPanel} aria-label="What's next">
          {hasShippedActions ? (
            <>
              <h2 className={styles.actionPanel__heading}>
                When the packet arrives
              </h2>
              <ol className={styles.actionPanel__steps}>
                <li>
                  <span className={styles.step_num}>1</span>
                  <span>
                    Sign in <strong>blue ink</strong> at every signature line
                  </span>
                </li>
                <li>
                  <span className={styles.step_num}>2</span>
                  <span>Include the filing fees per the packet instructions</span>
                </li>
                <li>
                  <span className={styles.step_num}>3</span>
                  <span>Mail the complete packet to USCIS</span>
                </li>
              </ol>
              {trackingPresent ? (
                <p className={styles.tracking_line}>
                  Tracking:{' '}
                  <span className={styles.tracking}>{trackingNumber}</span>
                </p>
              ) : null}
            </>
          ) : (
            <>
              <h2 className={styles.actionPanel__heading}>
                What you can do now
              </h2>
              <p className={styles.actionPanel__body}>
                Nothing — sit tight. We&apos;ll email you if anything needs
                your attention. Your filing is on track.
              </p>
            </>
          )}
        </section>

        {packetStoragePath ? (
          <section
            className={styles.packetPanel}
            aria-label="Your filing packet"
          >
            <h2 className={styles.packetPanel__heading}>
              Your filing packet
            </h2>
            <p className={styles.packetPanel__body}>
              <strong>{packetFileName ?? 'packet.pdf'}</strong>
              {(() => {
                const formatted = formatBytes(packetSizeBytes);
                return formatted !== null ? ` · ${formatted}` : '';
              })()}
              <span className={styles.packetPanel__hint}>
                — same packet we&apos;ll mail to you.
              </span>
            </p>
            <button
              type="button"
              className={styles.download_button}
              onClick={() => void handleDownloadClick()}
              disabled={downloading || !onDownloadPacket}
            >
              {downloading ? 'Preparing download…' : 'Download PDF →'}
            </button>
          </section>
        ) : null}
      </article>
    </main>
  );
}
